import { nodeListToArray } from '../helpers/nodeListToArray';

const navLoadedClass = 'nav-has-loaded';
const menuSlideStateAttribute = 'data-nav-expanded';
const navId = "navContainer";
const menuId = 'mobileNav';
const menuTriggerSelector = '#hamburger-menu-nav-tablet';

class MainNavigation {
    container: HTMLElement;
    navIsShowing: boolean;
    lastScrollPosition: number;
    hasLoaded: boolean;
    currentNavHeight: number;
    bodyEl: HTMLBodyElement;
    onPageAnchorLinks: HTMLAnchorElement[];
    menuTrigger: HTMLButtonElement;
    menuPanel: HTMLElement;
    menuIsOpen: boolean;
    subMenus: HTMLElement[];
    subMenuTriggers: HTMLButtonElement[];
    subMenusOpen: boolean = false;
    searchTrigger: HTMLButtonElement;
    searchContainer: HTMLElement;
    searchIsOpen: boolean;

    constructor(navigation: HTMLElement) {
        this.container = navigation;
        this.bodyEl = document.body as HTMLBodyElement;
        this.navIsShowing = true;
        this.hasLoaded = false;
        this.menuTrigger = this.container?.querySelector(menuTriggerSelector) as HTMLButtonElement;
        this.menuPanel = document.querySelector(`#${menuId}`) as HTMLElement;
        this.menuIsOpen = false;
        this.subMenus = this.container ? nodeListToArray(this.container?.querySelectorAll('[data-sub-nav]')) : [];
        this.subMenuTriggers = [];
        this.searchTrigger = document.querySelector('#search-button-mobile') as HTMLButtonElement;
        this.searchContainer = document.querySelector('.searchbar-container') as HTMLElement;
        this.searchIsOpen = false;

        // Intercept on-page links so that the offset recalculates before the browser reaches it
        // This makes sure that the target is always comfortably visible below the nav
        this.onPageAnchorLinks = [].slice.call(document.querySelectorAll('a[href^="#"]'));
        this.onPageAnchorLinks.forEach(link => {
            link.addEventListener('click', (e: Event) => {
                e.preventDefault();
                const hash = link.getAttribute('href');
                const target = document.querySelector(hash) as HTMLElement;
                if (target) {
                    if (this.navIsShowing && !this.menuIsOpen && target.offsetTop > window.pageYOffset) {
                        this.slideUp();
                    }
                    else if (!this.navIsShowing && target.offsetTop < window.pageYOffset) {
                        this.slideDown();
                    }
                    setTimeout(() => {
                        target.scrollIntoView(true);
                        target.focus();
                        window.location.hash = hash;
                    }, 0);
                }            
            })
        })
    }

    init = () => {
        //Activate Submenu Triggers
        if (this.subMenus.length > 0) {
            this.subMenus.forEach(subMenu => {
                const gatheredSubMenuTriggers = nodeListToArray(subMenu.querySelectorAll('[data-subnav-button]'));
                gatheredSubMenuTriggers.forEach(subMenuTrigger => {
                    this.subMenuTriggers.push(subMenuTrigger);
                });
            });
            if (this.subMenuTriggers.length > 0) {
                this.subMenuTriggers.forEach(subMenuTrigger => {
                    subMenuTrigger.addEventListener('click', () => subMenuTrigger.getAttribute('aria-expanded') === 'false' ? this.openSubMenu(subMenuTrigger) : this.closeSubMenu(subMenuTrigger));
                });
            }
        }

        document.body.addEventListener('keyup', (e: KeyboardEvent) => {
            if (e.which == 9) { // Tab key. Show nav if tabbed into
                const focusInNav = this.container.contains(document.activeElement);
                const subMenuLists = this.container ? nodeListToArray(this.container?.querySelectorAll('.header-nav-subnav')) : [];
                const focusInSubMenuList = subMenuLists.some(subMenuList => subMenuList.contains(document.activeElement));

                if (this.menuIsOpen) { // Escape key
                    this.closeMenu();
                    this.menuTrigger.focus();
                }

                if (!focusInSubMenuList && this.subMenusOpen) { // Escape key
                    this.closeAllSubMenus();
                }

                if (focusInNav && !this.navIsShowing) {
                    this.slideDown();
                }
            }
        });

        // Activate Menu Trigger
        this.menuTrigger?.addEventListener('click', () => this.menuIsOpen ? this.closeMenu() : this.openMenu());

        // Activate Search Trigger
        this.searchTrigger?.addEventListener('click', () => this.searchIsOpen ? this.closeSearch() : this.openSearch());
        
        // Setup on page load
        this.slideDown();
        this.setPageOffset();
        this.setupMultiColumnFallback();
    }

    setupMultiColumnFallback = () => {        
        // Detect Firefox
        const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
        
        // Apply fallback for browsers that don't support :has()
        if (isFirefox) {
            // Find all subnav lists and check if they have 5+ items
            const subnavLists = document.querySelectorAll('.header-nav-subnav-list');
            subnavLists.forEach(list => {
                const items = list.querySelectorAll('li');
                if (items.length >= 5) {
                    list.classList.add('multi-column');
                    list.classList.add('multi-column-firefox');
                    
                    // Also add a data attribute for debugging
                    list.setAttribute('data-multi-column', 'true');
                    list.setAttribute('data-item-count', items.length.toString());
                }
            });
        }
    }

    detectOutsideSubmenuClick = (e: MouseEvent) => {
        if (this.subMenusOpen && !this.menuIsOpen) {//If a submenu is open but it's not in a mobile menu
            if (!(e.target as HTMLElement).closest(`[data-sub-nav]`)) {
                this.closeAllSubMenus();
            }
        }
    }

    showElement = (element: HTMLElement) => {
        (element as any).removeAttribute('inert');
    }

    hideElement = (element: HTMLElement) => {
        (element as any).inert = true;
    }

    closeAllSubMenus = () => {
        const openSubMenus = this.subMenuTriggers.filter(subMenuTrigger => subMenuTrigger.getAttribute('aria-expanded') === 'true');
        openSubMenus.forEach(subMenuTrigger => {
            this.closeSubMenu(subMenuTrigger);
        });
        this.subMenusOpen = false;
    }

    closeSubMenu = (subMenuTrigger: HTMLButtonElement) => {
        const subMenuToClose = document.getElementById(subMenuTrigger.getAttribute('aria-controls'));
        subMenuTrigger.setAttribute('aria-expanded', 'false');
        this.hideElement(subMenuToClose);
        subMenuToClose.classList.add('visually-hidden');
        this.subMenusOpen = false;
        document.removeEventListener("click", this.detectOutsideSubmenuClick.bind(this));
    }

    openSubMenu = (subMenuTrigger: HTMLButtonElement) => {
        this.closeAllSubMenus();
        const subMenuToOpen = document.getElementById(subMenuTrigger.getAttribute('aria-controls'));
        subMenuTrigger.setAttribute('aria-expanded', 'true');
        this.showElement(subMenuToOpen);
        subMenuToOpen.classList.remove('visually-hidden');
        this.subMenusOpen = true;
        document.addEventListener("click", this.detectOutsideSubmenuClick.bind(this));
    }
    
    openMenu = () => {
        this.closeSearch();
        this.menuTrigger.setAttribute('aria-expanded', 'true');
        this.menuTrigger.classList.add('opened');
        this.showElement(this.menuPanel);
        this.menuPanel.classList.add('show-nav');
        this.menuPanel.classList.remove('visually-hidden');
        this.bodyEl.style.overflowY = 'hidden';
        this.menuIsOpen = true;
    }
    
    closeMenu = () => {
        this.menuTrigger.setAttribute('aria-expanded', 'false');
        this.menuTrigger.classList.remove('opened');
        this.hideElement(this.menuPanel);
        this.menuPanel.classList.add('visually-hidden');
        this.menuPanel.classList.remove('show-nav'); 
        this.bodyEl.style.overflowY = '';
        this.menuIsOpen = false;
    }

    openSearch = () => {
        this.closeMenu();
        if (this.searchTrigger && this.searchContainer) {
            this.searchTrigger.classList.add('opened');
            this.searchContainer.classList.add('opened');
            this.searchIsOpen = true;
        }
    }

    closeSearch = () => {
        if (this.searchTrigger && this.searchContainer) {
            this.searchTrigger.classList.remove('opened');
            this.searchContainer.classList.remove('opened');
            this.searchIsOpen = false;
        }
    }

    slideDown = () => {
        if (this.container.getAttribute('data-skip-slide-down') === 'true') {
            return;
        }

        this.navIsShowing = true;
        this.container.setAttribute(menuSlideStateAttribute, 'true');
        this.setCssVariables();
    }

    slideUp = () => {
        this.closeAllSubMenus();
        this.closeSearch();
        this.navIsShowing = false;
        this.container.setAttribute(menuSlideStateAttribute, 'false');
        this.setCssVariables();
    }

    setPageOffset = () => {
        if (!this.hasLoaded) {
            this.container.classList.add(navLoadedClass);
            this.hasLoaded = true;
        }
        this.currentNavHeight = this.container.offsetHeight;
        this.setCssVariables();
    }

    scrollHandler = () => {
        if (this.hasLoaded) {
            const currentPosition = window.pageYOffset;
            const delta = currentPosition - this.lastScrollPosition;
            this.lastScrollPosition = currentPosition;
            if (this.navIsShowing && !this.menuIsOpen) {
                if (delta > 0 && currentPosition > this.currentNavHeight) {
                    this.slideUp();
                }
            }
            else if (delta < 0 || currentPosition < this.currentNavHeight) {
                this.slideDown();
            }
        }
    }

    resizeHandler = () => {
        this.closeAllSubMenus();
        this.closeMenu();
        this.closeSearch();
        this.setPageOffset();
    }

    setCssVariables = () => {
        document.documentElement.style.setProperty('--nav-clearance', `${this.navIsShowing ? this.currentNavHeight : this.currentNavHeight}px`);
        document.documentElement.style.setProperty('--nav-margin-offset', `${this.navIsShowing ? this.currentNavHeight : 0}px`);
    }
}

let mainNavigation: MainNavigation = null;
const navContainer = document.querySelector(`#${navId}`) as HTMLElement;

if (navContainer) {
    mainNavigation = new MainNavigation(navContainer);
    (window as any).mainNavigation = mainNavigation;
}

export { mainNavigation };